import { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Plus, PencilSimple, Trash, ArrowClockwise, CircleDashed, Square, Rectangle, MagnifyingGlassPlus, UsersThree, Timer, ChartLineUp, CalendarBlank, CaretLeft, CaretRight, Users, Phone, NotePencil, FloppyDisk, FolderOpen, Lock, LockOpen } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useRestaurant } from "../contexts/RestaurantContext";

const STATUS_COLORS = {
  available: "#81B29A",
  occupied: "#E07A5F",
  reserved: "#F4A261",
  blocked: "#9CA3AF",
};

const TIER_CONFIG = {
  gold: { label: "Gold", bg: "#D4AF3720", color: "#B8860B", border: "#D4AF37" },
  silver: { label: "Silver", bg: "#C0C0C020", color: "#6B7280", border: "#A8A8A8" },
  bronze: { label: "Bronze", bg: "#CD7F3220", color: "#A0522D", border: "#CD7F32" },
};
const TierBadge = ({ tier }) => {
  if (!tier || !TIER_CONFIG[tier]) return null;
  const c = TIER_CONFIG[tier];
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border" style={{ backgroundColor: c.bg, color: c.color, borderColor: c.border }} data-testid={`tier-badge-${tier}`}>{c.label}</span>;
};

const TABLE_PRESETS = [
  { label: "Round 2", shape: "round", seats: 2, width: 60, height: 60 },
  { label: "Round 4", shape: "round", seats: 4, width: 80, height: 80 },
  { label: "Square 4", shape: "square", seats: 4, width: 80, height: 80 },
  { label: "Rect 6", shape: "rectangle", seats: 6, width: 120, height: 70 },
  { label: "Rect 8", shape: "rectangle", seats: 8, width: 160, height: 70 },
  { label: "Large 10", shape: "rectangle", seats: 10, width: 200, height: 80 },
];

export default function FloorPlanPage() {
  const { restId } = useRestaurant();
  const [floors, setFloors] = useState([]);
  const [activeFloor, setActiveFloor] = useState(null);
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [servers, setServers] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showAddTable, setShowAddTable] = useState(false);
  const [showEditTable, setShowEditTable] = useState(false);
  const [showAddFloor, setShowAddFloor] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showSeatingSuggest, setShowSeatingSuggest] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [partySize, setPartySize] = useState(2);
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [newFloorName, setNewFloorName] = useState("");
  const [newTablePreset, setNewTablePreset] = useState(0);
  const [newTableName, setNewTableName] = useState("");
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionColor, setNewSectionColor] = useState("#81B29A");
  const [newSectionServer, setNewSectionServer] = useState("");
  const [editForm, setEditForm] = useState({});
  const [tableAnalytics, setTableAnalytics] = useState(null);
  const [showReservations, setShowReservations] = useState(true);
  const [floorReservations, setFloorReservations] = useState([]);
  const [tableTimers, setTableTimers] = useState({});
  const [tableNotes, setTableNotes] = useState([]);
  const [allTableNotes, setAllTableNotes] = useState({});
  const [newNoteText, setNewNoteText] = useState("");
  const [dropTargetTable, setDropTargetTable] = useState(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showLoadTemplate, setShowLoadTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [assignMode, setAssignMode] = useState(false);
  const [assignSection, setAssignSection] = useState(null);
  const [serviceState, setServiceState] = useState("idle");
  const [unlockedTables, setUnlockedTables] = useState(new Set());
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);
  const canvasRef = useRef(null);

  const fetchFloors = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/floors${params}`);
      setFloors(data);
      if (data.length > 0 && !activeFloor) setActiveFloor(data[0].id);
      if (data.length > 0 && activeFloor && !data.find((f) => f.id === activeFloor)) setActiveFloor(data[0].id);
    } catch (err) {
      toast.error("Failed to load floors");
    }
  }, [activeFloor, restId]);

  const fetchTables = useCallback(async () => {
    if (!activeFloor) return;
    try {
      const { data } = await api.get(`/floors/${activeFloor}/tables`);
      setTables(data);
    } catch (err) {
      if (err.response?.status !== 404) toast.error("Failed to load tables");
    }
  }, [activeFloor]);

  const fetchSections = useCallback(async () => {
    if (!activeFloor) return;
    try {
      const { data } = await api.get(`/sections?floor_id=${activeFloor}`);
      setSections(data);
    } catch (err) { /* silent */ }
  }, [activeFloor]);

  const fetchServers = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/servers${params}`);
      setServers(data);
    } catch (err) { /* silent */ }
  }, [restId]);

  useEffect(() => { fetchFloors(); fetchServers(); }, [fetchFloors, fetchServers]);
  useEffect(() => { fetchTables(); fetchSections(); }, [fetchTables, fetchSections]);

  // Fetch service state
  useEffect(() => {
    const fetchServiceState = async () => {
      try {
        const params = restId ? `?restaurant_id=${restId}` : "";
        const { data } = await api.get(`/service/status${params}`);
        setServiceState(data.state);
      } catch { /* silent */ }
    };
    fetchServiceState();
  }, [restId]);

  // Fetch today's reservations (visible until post-service reset)
  const fetchReservations = useCallback(async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const { data } = await api.get(`/reservations?date=${today}`);
      setFloorReservations(data.filter((r) => r.status !== "cancelled"));
    } catch { /* silent */ }
  }, []);

  const [floorTiers, setFloorTiers] = useState({});
  const fetchFloorTiers = useCallback(async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const { data } = await api.get(`/reservations/tiers?date=${today}`);
      setFloorTiers(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchReservations(); }, [fetchReservations]);
  useEffect(() => { fetchFloorTiers(); }, [fetchFloorTiers]);

  // Table turn timers — refresh every 30s
  const fetchTimers = useCallback(async () => {
    try {
      const { data } = await api.get("/analytics/table-timers");
      setTableTimers(data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchTimers(); }, [fetchTimers]);
  useEffect(() => {
    const interval = setInterval(fetchTimers, 30000);
    return () => clearInterval(interval);
  }, [fetchTimers]);

  // Table notes
  const fetchTableNotes = useCallback(async () => {
    if (!selectedTable) return;
    try {
      const { data } = await api.get(`/table-notes?table_id=${selectedTable.id}`);
      setTableNotes(data);
    } catch { /* silent */ }
  }, [selectedTable]);
  useEffect(() => { fetchTableNotes(); }, [fetchTableNotes]);

  // All table notes (for badges on floor plan)
  const fetchAllTableNotes = useCallback(async () => {
    try {
      const { data } = await api.get("/table-notes");
      const byTable = {};
      data.forEach((n) => {
        if (!byTable[n.table_id]) byTable[n.table_id] = [];
        byTable[n.table_id].push(n);
      });
      setAllTableNotes(byTable);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchAllTableNotes(); }, [fetchAllTableNotes]);

  const addTableNote = async () => {
    if (!newNoteText.trim() || !selectedTable) return;
    try {
      await api.post("/table-notes", { table_id: selectedTable.id, text: newNoteText.trim() });
      setNewNoteText("");
      fetchTableNotes();
      fetchAllTableNotes();
    } catch { /* silent */ }
  };

  const deleteTableNote = async (noteId) => {
    try {
      await api.delete(`/table-notes/${noteId}`);
      fetchTableNotes();
      fetchAllTableNotes();
    } catch { /* silent */ }
  };

  // WebSocket for real-time
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "table_update" || msg.type === "table_move") fetchTables();
        if (msg.type === "floor_update") fetchFloors();
        if (msg.type === "section_update") fetchSections();
        if (msg.type === "reservation_update") fetchReservations();
      };
      ws.onclose = () => setTimeout(() => {}, 3000);
    } catch { /* silent */ }
    return () => ws?.close();
  }, [fetchTables, fetchFloors, fetchSections, fetchReservations]);

  // ─── Drag Logic (mouse + touch) ───
  const getCanvasCoords = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: clientX - rect.left + canvas.scrollLeft,
      y: clientY - rect.top + canvas.scrollTop,
    };
  };

  const startDrag = (clientX, clientY, table) => {
    const coords = getCanvasCoords(clientX, clientY);
    setDragState({
      tableId: table.id,
      offsetX: coords.x - table.x,
      offsetY: coords.y - table.y,
    });
    setSelectedTable(table);
  };

  const isTableLocked = useCallback((tableId) => {
    if (serviceState === "idle" || serviceState === "pre_service") return false;
    return !unlockedTables.has(tableId);
  }, [serviceState, unlockedTables]);

  const handleMouseDown = (e, table) => {
    if (isTableLocked(table.id)) return;
    e.stopPropagation();
    startDrag(e.clientX, e.clientY, table);
  };

  const handleTouchStart = (e, table) => {
    if (isTableLocked(table.id)) return;
    e.stopPropagation();
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY, table);
  };

  const moveDrag = useCallback((clientX, clientY) => {
    if (!dragState) return;
    const coords = getCanvasCoords(clientX, clientY);
    const x = Math.max(0, coords.x - dragState.offsetX);
    const y = Math.max(0, coords.y - dragState.offsetY);
    setTables((prev) => prev.map((t) => t.id === dragState.tableId ? { ...t, x, y } : t));
  }, [dragState]);

  const handleMouseMove = useCallback((e) => {
    moveDrag(e.clientX, e.clientY);
  }, [moveDrag]);

  const handleTouchMove = useCallback((e) => {
    if (!dragState) return;
    e.preventDefault();
    const touch = e.touches[0];
    moveDrag(touch.clientX, touch.clientY);
  }, [dragState, moveDrag]);

  const handleMouseUp = useCallback(async () => {
    if (!dragState) return;
    const table = tables.find((t) => t.id === dragState.tableId);
    if (table) {
      try {
        await api.patch(`/tables/${dragState.tableId}/position`, { x: table.x, y: table.y });
      } catch { toast.error("Failed to save position"); }
    }
    setDragState(null);
  }, [dragState, tables]);

  useEffect(() => {
    if (dragState) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp, handleTouchMove]);

  // ─── Resize Logic ───
  const startResize = (e, table, handle) => {
    e.stopPropagation();
    e.preventDefault();
    const coords = getCanvasCoords(e.clientX, e.clientY);
    setResizeState({
      tableId: table.id,
      handle,
      startX: coords.x,
      startY: coords.y,
      startW: table.width,
      startH: table.height,
      startTX: table.x,
      startTY: table.y,
      isRound: table.shape === "round",
    });
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizeState) return;
    const coords = getCanvasCoords(e.clientX, e.clientY);
    const dx = coords.x - resizeState.startX;
    const dy = coords.y - resizeState.startY;
    setTables((prev) => prev.map((t) => {
      if (t.id !== resizeState.tableId) return t;
      let newW = resizeState.startW;
      let newH = resizeState.startH;
      let newX = resizeState.startTX;
      let newY = resizeState.startTY;
      const h = resizeState.handle;
      if (h.includes("e")) newW = Math.max(40, resizeState.startW + dx);
      if (h.includes("w")) { newW = Math.max(40, resizeState.startW - dx); newX = resizeState.startTX + (resizeState.startW - newW); }
      if (h.includes("s")) newH = Math.max(40, resizeState.startH + dy);
      if (h.includes("n")) { newH = Math.max(40, resizeState.startH - dy); newY = resizeState.startTY + (resizeState.startH - newH); }
      if (resizeState.isRound) { const size = Math.max(newW, newH); newW = size; newH = size; }
      return { ...t, width: newW, height: newH, x: newX, y: newY };
    }));
  }, [resizeState]);

  const handleResizeEnd = useCallback(async () => {
    if (!resizeState) return;
    const table = tables.find((t) => t.id === resizeState.tableId);
    if (table) {
      try {
        await Promise.all([
          api.patch(`/tables/${table.id}/size`, { width: table.width, height: table.height }),
          api.patch(`/tables/${table.id}/position`, { x: table.x, y: table.y }),
        ]);
        setSelectedTable(table);
      } catch { toast.error("Failed to save size"); }
    }
    setResizeState(null);
  }, [resizeState, tables]);

  useEffect(() => {
    if (resizeState) {
      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeEnd);
      return () => {
        window.removeEventListener("mousemove", handleResizeMove);
        window.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [resizeState, handleResizeMove, handleResizeEnd]);

  // ─── Canvas Pan & Zoom ───
  const handleCanvasWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setCanvasZoom((z) => Math.min(3, Math.max(0.3, z + delta)));
  }, []);

  const handleCanvasPanStart = useCallback((e) => {
    // Only pan if clicking on empty canvas (not a table)
    if (e.target !== canvasRef.current && !e.target.classList.contains("floor-canvas")) return;
    if (e.touches) {
      if (e.touches.length === 1) {
        setIsPanning(true);
        setPanStart({ x: e.touches[0].clientX - canvasPan.x, y: e.touches[0].clientY - canvasPan.y });
      }
    } else {
      setIsPanning(true);
      setPanStart({ x: e.clientX - canvasPan.x, y: e.clientY - canvasPan.y });
    }
  }, [canvasPan]);

  const handleCanvasPanMove = useCallback((e) => {
    if (!isPanning || !panStart) return;
    if (e.touches) {
      setCanvasPan({ x: e.touches[0].clientX - panStart.x, y: e.touches[0].clientY - panStart.y });
    } else {
      setCanvasPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
  }, [isPanning, panStart]);

  const handleCanvasPanEnd = useCallback(() => {
    setIsPanning(false);
    setPanStart(null);
  }, []);

  // ─── Actions ───
  const addFloor = async () => {
    if (!newFloorName.trim()) return;
    try {
      const { data } = await api.post("/floors", { name: newFloorName, order: floors.length, restaurant_id: restId });
      setFloors((p) => [...p, data]);
      setActiveFloor(data.id);
      setNewFloorName("");
      setShowAddFloor(false);
      toast.success("Floor created");
    } catch { toast.error("Failed to create floor"); }
  };

  const deleteFloor = async (id) => {
    try {
      await api.delete(`/floors/${id}`);
      setFloors((p) => p.filter((f) => f.id !== id));
      if (activeFloor === id) setActiveFloor(floors.find((f) => f.id !== id)?.id || null);
      toast.success("Floor deleted");
    } catch { toast.error("Failed to delete floor"); }
  };

  const addTable = async () => {
    if (!activeFloor || !newTableName.trim()) return;
    const preset = TABLE_PRESETS[newTablePreset];
    try {
      const { data } = await api.post("/tables", {
        floor_id: activeFloor, name: newTableName,
        shape: preset.shape, seats: preset.seats,
        width: preset.width, height: preset.height,
        x: 100 + Math.random() * 200, y: 100 + Math.random() * 200,
      });
      setTables((p) => [...p, data]);
      setNewTableName("");
      setShowAddTable(false);
      toast.success("Table added");
    } catch { toast.error("Failed to add table"); }
  };

  const deleteTable = async (id) => {
    try {
      await api.delete(`/tables/${id}`);
      setTables((p) => p.filter((t) => t.id !== id));
      if (selectedTable?.id === id) setSelectedTable(null);
      toast.success("Table deleted");
    } catch { toast.error("Failed to delete table"); }
  };

  const rotateTable = async (table) => {
    const newRot = ((table.rotation || 0) + 45) % 360;
    try {
      await api.put(`/tables/${table.id}`, { rotation: newRot });
      setTables((p) => p.map((t) => t.id === table.id ? { ...t, rotation: newRot } : t));
      if (selectedTable?.id === table.id) setSelectedTable({ ...table, rotation: newRot });
    } catch { toast.error("Failed to rotate"); }
  };

  const openEditTable = (table) => {
    setEditForm({ name: table.name, seats: table.seats, shape: table.shape, status: table.status, section_id: table.section_id || "" });
    setShowEditTable(true);
  };

  const saveEditTable = async () => {
    if (!selectedTable) return;
    try {
      const payload = { ...editForm };
      if (!payload.section_id) payload.section_id = null;
      const { data } = await api.put(`/tables/${selectedTable.id}`, payload);
      setTables((p) => p.map((t) => t.id === selectedTable.id ? { ...t, ...data } : t));
      setSelectedTable({ ...selectedTable, ...data });
      setShowEditTable(false);
      toast.success("Table updated");
    } catch { toast.error("Failed to update table"); }
  };

  const addSection = async () => {
    if (!activeFloor || !newSectionName.trim()) return;
    try {
      const { data } = await api.post("/sections", {
        floor_id: activeFloor, name: newSectionName,
        color: newSectionColor, server_id: newSectionServer || null,
      });
      setSections((p) => [...p, data]);
      setNewSectionName("");
      setShowAddSection(false);
      toast.success("Section created");
    } catch { toast.error("Failed to create section"); }
  };

  const getSeatingSuggestions = async () => {
    try {
      const { data } = await api.get(`/seating/suggest?party_size=${partySize}&floor_id=${activeFloor || ""}`);
      setSuggestions(data);
      setShowSeatingSuggest(true);
    } catch { toast.error("Failed to get suggestions"); }
  };

  const getSectionForTable = (table) => sections.find((s) => s.id === table.section_id);

  // ─── Reservation Drag & Drop onto Tables ───
  const handleResDragStart = (e, reservation) => {
    e.dataTransfer.setData("reservation_id", reservation.id);
    e.dataTransfer.setData("reservation_name", reservation.guest_name);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleTableDragOver = (e, table) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetTable(table.id);
  };

  const handleTableDragLeave = () => {
    setDropTargetTable(null);
  };

  const handleTableDrop = async (e, table) => {
    e.preventDefault();
    setDropTargetTable(null);
    const resId = e.dataTransfer.getData("reservation_id");
    if (!resId) return;
    const resName = e.dataTransfer.getData("reservation_name");
    // Check if this reservation already has tables assigned (combining)
    const existingRes = floorReservations.find((r) => r.id === resId);
    const existingIds = existingRes?.table_ids || [];
    if (existingRes?.table_id && !existingIds.includes(existingRes.table_id)) {
      existingIds.push(existingRes.table_id);
    }
    const newIds = [...new Set([...existingIds, table.id])];
    try {
      await api.put(`/reservations/${resId}`, {
        table_ids: newIds,
        table_id: newIds[0],
        floor_id: activeFloor,
        status: "seated",
      });
      // Snap combined tables together visually
      if (newIds.length > 1) {
        await api.post("/tables/combine", { table_ids: newIds });
      }
      fetchTables();
      fetchReservations();
      toast.success(newIds.length > 1
        ? `${resName} seated at ${newIds.length} combined tables`
        : `${resName} seated at ${table.name}`
      );
    } catch {
      toast.error("Failed to assign reservation");
    }
  };

  const unassignReservation = async (resId) => {
    try {
      await api.put(`/reservations/${resId}`, { table_id: null, table_ids: [], status: "confirmed" });
      fetchTables();
      fetchReservations();
      toast.success("Reservation unassigned");
    } catch { toast.error("Failed"); }
  };

  const deleteReservation = async (resId) => {
    try {
      await api.delete(`/reservations/${resId}`);
      fetchTables();
      fetchReservations();
      toast.success("Reservation deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const getResForTable = (tableId) => floorReservations.find((r) => {
    const ids = r.table_ids || [];
    return (ids.includes(tableId) || r.table_id === tableId) && (r.status === "seated" || r.status === "confirmed");
  });

  const to12h = (t) => t || "";

  const getCombinedTables = (res) => {
    if (!res) return [];
    const ids = new Set(res.table_ids || []);
    if (res.table_id) ids.add(res.table_id);
    return tables.filter((t) => ids.has(t.id));
  };

  // ─── Pre-Service Section Assignment ───
  const handleTableClick = async (e, table) => {
    e.stopPropagation();
    if (assignMode && assignSection) {
      // Toggle table section assignment
      const currentSection = table.section_id;
      const newSectionId = currentSection === assignSection.id ? null : assignSection.id;
      try {
        await api.put(`/tables/${table.id}`, { section_id: newSectionId });
        setTables((prev) => prev.map((t) => t.id === table.id ? { ...t, section_id: newSectionId } : t));
        toast.success(newSectionId ? `${table.name} added to ${assignSection.name}` : `${table.name} removed from section`);
      } catch { toast.error("Failed to update"); }
    } else {
      setSelectedTable(table);
    }
  };

  const getAssignGlow = (table) => {
    if (!assignMode || !assignSection) return null;
    if (table.section_id === assignSection.id) return assignSection.color;
    return null;
  };

  // ─── Floorplan Templates ───
  const fetchTemplates = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/floorplan-templates${params}`);
      setTemplates(data);
    } catch { /* silent */ }
  }, [restId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const saveTemplate = async () => {
    if (!templateName.trim() || !activeFloor) return;
    try {
      await api.post("/floorplan-templates", { name: templateName, floor_id: activeFloor, description: templateDesc });
      setShowSaveTemplate(false);
      setTemplateName("");
      setTemplateDesc("");
      fetchTemplates();
      toast.success("Layout saved as template");
    } catch { toast.error("Failed to save template"); }
  };

  const loadTemplate = async (tmplId) => {
    if (!activeFloor) return;
    try {
      const { data } = await api.post(`/floorplan-templates/${tmplId}/load?floor_id=${activeFloor}`);
      toast.success(`Loaded ${data.tables_loaded} tables, ${data.sections_loaded} sections`);
      setShowLoadTemplate(false);
      fetchTables();
      fetchSections();
    } catch { toast.error("Failed to load template"); }
  };

  const deleteTemplate = async (tmplId) => {
    try {
      await api.delete(`/floorplan-templates/${tmplId}`);
      fetchTemplates();
      toast.success("Template deleted");
    } catch { toast.error("Failed to delete template"); }
  };

  // ─── Visual Section Regions ───
  const getSectionRegion = (section) => {
    const sectionTables = tables.filter((t) => t.section_id === section.id);
    if (sectionTables.length === 0) return null;
    const pad = 32;
    const minX = Math.min(...sectionTables.map((t) => t.x)) - pad;
    const minY = Math.min(...sectionTables.map((t) => t.y)) - pad;
    const maxX = Math.max(...sectionTables.map((t) => t.x + t.width)) + pad;
    const maxY = Math.max(...sectionTables.map((t) => t.y + t.height)) + pad;
    const server = servers.find((s) => s.id === section.server_id);
    return { minX, minY, width: maxX - minX, height: maxY - minY, server };
  };

  // Fetch per-table analytics when selected
  useEffect(() => {
    if (!selectedTable) { setTableAnalytics(null); return; }
    const fetchTableAnalytics = async () => {
      try {
        const { data } = await api.get(`/tables/${selectedTable.id}/analytics?days=7`);
        setTableAnalytics(data);
      } catch { setTableAnalytics(null); }
    };
    fetchTableAnalytics();
  }, [selectedTable?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTableShapeStyle = (table) => ({
    borderRadius: table.shape === "round" ? "50%" : "8px",
  });

  // Generate seat positions around a table
  const getSeatPositions = (table) => {
    const seats = table.seats || 4;
    const w = table.width || 80;
    const h = table.height || 80;
    const seatSize = 14;
    const gap = 4; // gap from table edge
    const positions = [];

    if (table.shape === "round") {
      // Distribute evenly around a circle
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.max(w, h) / 2 + gap + seatSize / 2;
      for (let i = 0; i < seats; i++) {
        const angle = (i / seats) * Math.PI * 2 - Math.PI / 2;
        positions.push({
          left: cx + Math.cos(angle) * radius - seatSize / 2,
          top: cy + Math.sin(angle) * radius - seatSize / 2,
        });
      }
    } else {
      // Rectangle/square: distribute around edges
      // Top, right, bottom, left
      const perSide = {
        top: Math.ceil(seats * (w / (2 * (w + h)))),
        bottom: Math.ceil(seats * (w / (2 * (w + h)))),
        left: 0,
        right: 0,
      };
      perSide.left = Math.ceil((seats - perSide.top - perSide.bottom) / 2);
      perSide.right = seats - perSide.top - perSide.bottom - perSide.left;

      // Top edge
      for (let i = 0; i < perSide.top; i++) {
        const spacing = w / (perSide.top + 1);
        positions.push({ left: spacing * (i + 1) - seatSize / 2, top: -(gap + seatSize) });
      }
      // Bottom edge
      for (let i = 0; i < perSide.bottom; i++) {
        const spacing = w / (perSide.bottom + 1);
        positions.push({ left: spacing * (i + 1) - seatSize / 2, top: h + gap });
      }
      // Left edge
      for (let i = 0; i < perSide.left; i++) {
        const spacing = h / (perSide.left + 1);
        positions.push({ left: -(gap + seatSize), top: spacing * (i + 1) - seatSize / 2 });
      }
      // Right edge
      for (let i = 0; i < perSide.right; i++) {
        const spacing = h / (perSide.right + 1);
        positions.push({ left: w + gap, top: spacing * (i + 1) - seatSize / 2 });
      }
    }
    return positions;
  };

  return (
    <div className="h-full flex flex-col" data-testid="floor-plan-page">
      {/* ─── Header (compact on iPad) ─── */}
      <div className="flex items-center justify-between px-2 sm:px-5 py-1 sm:py-2 bg-white border-b border-stone-200 gap-1 sm:gap-2">
        <div className="flex items-center gap-1.5 sm:gap-3 overflow-x-auto">
          <h2 className="text-sm sm:text-lg font-semibold tracking-tight whitespace-nowrap" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>
            Floor Plan
          </h2>
          {/* Floor tabs - hidden on tablet/mobile, shown at bottom instead */}
          <div className="hidden xl:flex items-center gap-1.5">
          {floors.length > 0 && (
            <Tabs value={activeFloor || ""} onValueChange={setActiveFloor}>
              <TabsList className="h-auto p-0.5 bg-stone-100" data-testid="floor-tabs">
                {floors.map((f) => (
                  <TabsTrigger key={f.id} value={f.id} className="px-2 py-1 text-xs rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid={`floor-tab-${f.name.toLowerCase().replace(/\s/g, "-")}`}>
                    {f.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
          <Button variant="ghost" size="sm" onClick={() => setShowAddFloor(true)} data-testid="add-floor-btn" className="text-stone-500 hover:text-stone-800 whitespace-nowrap text-xs h-7">
            <Plus size={14} /> Floor
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setTemplateName(""); setTemplateDesc(""); setShowSaveTemplate(true); }} data-testid="save-layout-btn" className="text-stone-500 hover:text-stone-800 whitespace-nowrap text-xs h-7">
            <FloppyDisk size={14} className="mr-0.5" /> <span className="hidden lg:inline">Save</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { fetchTemplates(); setShowLoadTemplate(true); }} data-testid="load-layout-btn" className="text-stone-500 hover:text-stone-800 whitespace-nowrap text-xs h-7">
            <FolderOpen size={14} className="mr-0.5" /> <span className="hidden lg:inline">Load</span>
          </Button>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant={showReservations ? "default" : "outline"}
            size="sm"
            onClick={() => setShowReservations(!showReservations)}
            data-testid="toggle-reservations-btn"
            className="text-xs h-7"
            style={showReservations ? { backgroundColor: "#2F4F4F" } : {}}
          >
            <CalendarBlank size={14} className="mr-0.5" />
            <span className="hidden sm:inline">Res</span>
            {floorReservations.length > 0 && (
              <span className="ml-0.5 text-[9px] bg-white/20 rounded-full px-1">{floorReservations.length}</span>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAddSection(true)} data-testid="add-section-btn" className="text-xs h-7">
            <UsersThree size={14} className="mr-0.5" /> <span className="hidden sm:inline">Section</span>
          </Button>
          <Button variant="outline" size="sm" onClick={getSeatingSuggestions} data-testid="smart-seating-btn" className="text-xs h-7">
            <MagnifyingGlassPlus size={14} className="mr-0.5" /> <span className="hidden lg:inline">Smart Seat</span>
          </Button>
        </div>
      </div>

      {/* ─── Pre-Service Section Assignment Toolbar ─── */}
      {assignMode && (
        <div className="flex items-center gap-3 px-3 sm:px-5 py-2 bg-amber-50 border-b border-amber-200" data-testid="assign-toolbar">
          <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Assign Mode</span>
          <div className="flex items-center gap-1.5 flex-1 overflow-x-auto">
            {sections.map((s) => {
              const srv = servers.find((sv) => sv.id === s.server_id);
              const isActive = assignSection?.id === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setAssignSection(isActive ? null : s)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${isActive ? "ring-2 ring-offset-1 text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"}`}
                  style={isActive ? { backgroundColor: s.color, ringColor: s.color } : {}}
                  data-testid={`assign-section-${s.id}`}
                >
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: isActive ? "#fff" : s.color }} />
                  {s.name}
                  {srv && <span className={`text-[9px] ${isActive ? "text-white/70" : "text-stone-500"}`}>({srv.name})</span>}
                </button>
              );
            })}
            {sections.length === 0 && (
              <span className="text-xs text-amber-600">Create a section first using the Section button above</span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => { setAssignMode(false); setAssignSection(null); }} className="text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="exit-assign-mode-btn">
            Done
          </Button>
        </div>
      )}

      {/* ─── Section Assign Button (show in pre-service or anytime) ─── */}
      {!assignMode && (
        <div className="flex items-center gap-2 px-3 sm:px-5 py-1.5 bg-stone-50 border-b border-stone-200">
          <Button variant="ghost" size="sm" onClick={() => setAssignMode(true)} className="text-xs text-stone-500 hover:text-[#2F4F4F]" data-testid="enter-assign-mode-btn">
            <UsersThree size={14} className="mr-1" /> Assign Tables to Sections
          </Button>
          {sections.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              {sections.map((s) => {
                const count = tables.filter((t) => t.section_id === s.id).length;
                return (
                  <div key={s.id} className="flex items-center gap-1 text-[10px] text-stone-500">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                    <span>{s.name}: {count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* ─── Reservations Panel ─── */}
        {showReservations && (
          <div className="w-[260px] sm:w-[300px] flex-shrink-0 bg-white border-r border-stone-200 flex flex-col animate-slide-in" data-testid="reservations-panel">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Today's Reservations</h3>
                <p className="text-[10px] text-stone-500 mt-0.5">{format(new Date(), "EEEE, MMM d")} &middot; Drag onto a table to seat</p>
              </div>
              <button onClick={() => setShowReservations(false)} className="p-1 rounded hover:bg-stone-100 text-stone-500" data-testid="close-reservations-panel">
                <CaretLeft size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="reservations-list">
              {floorReservations.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarBlank size={32} className="mx-auto mb-2 text-stone-300" weight="duotone" />
                  <p className="text-xs text-stone-500">No active reservations today</p>
                </div>
              ) : (
                <>
                  {/* Unassigned reservations (draggable) */}
                  {floorReservations.filter((r) => !r.table_id && r.status === "confirmed").length > 0 && (
                    <div className="mb-1">
                      <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold px-1 mb-1.5">Unassigned</p>
                      {floorReservations.filter((r) => !r.table_id && r.status === "confirmed").map((res) => (
                        <div
                          key={res.id}
                          draggable
                          onDragStart={(e) => handleResDragStart(e, res)}
                          className="res-card mb-2 p-3 rounded-lg border border-stone-200 bg-white cursor-grab hover:shadow-md hover:border-stone-300 transition-all active:cursor-grabbing"
                          data-testid={`res-drag-card-${res.id}`}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-sm text-stone-800" style={{ fontFamily: "Work Sans" }}>{res.guest_name}</span>
                              <TierBadge tier={floorTiers[res.guest_name]} />
                            </div>
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: "#2F4F4F15", color: "#2F4F4F" }}>{to12h(res.time)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-stone-500">
                            <span className="flex items-center gap-0.5"><Users size={11} /> {res.party_size}</span>
                            {res.guest_phone && <span className="flex items-center gap-0.5"><Phone size={11} /> {res.guest_phone}</span>}
                          </div>
                          {res.notes && (
                            <div className="mt-1.5 flex items-start gap-1 text-[11px] text-stone-500">
                              <NotePencil size={11} className="mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{res.notes}</span>
                            </div>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            <div className="text-[10px] text-stone-300 italic">Drag to a table to seat</div>
                            <button onClick={(e) => { e.stopPropagation(); deleteReservation(res.id); }} className="text-[10px] text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-res-drag-${res.id}`}>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Seated reservations (already assigned) */}
                  {floorReservations.filter((r) => (r.table_id || r.status === "seated") && r.status !== "completed").length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold px-1 mb-1.5">Seated</p>
                      {floorReservations.filter((r) => (r.table_id || r.status === "seated") && r.status !== "completed").map((res) => {
                        const assignedTable = tables.find((t) => t.id === res.table_id);
                        return (
                          <div key={res.id} className="mb-2 p-3 rounded-lg border border-stone-100 bg-stone-50" data-testid={`res-seated-card-${res.id}`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-sm text-stone-700" style={{ fontFamily: "Work Sans" }}>{res.guest_name}</span>
                                <TierBadge tier={floorTiers[res.guest_name]} />
                              </div>
                              <Badge style={{ backgroundColor: "#81B29A20", color: "#81B29A", border: "none" }} className="text-[10px]">Seated</Badge>
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-stone-500 flex-wrap">
                              <span className="flex items-center gap-0.5"><Users size={11} /> {res.party_size}</span>
                              <span>{to12h(res.time)}</span>
                              {(() => {
                                const combined = getCombinedTables(res);
                                if (combined.length > 1) {
                                  return <span className="font-medium text-stone-700">@ {combined.map((t) => t.name).join(" + ")}</span>;
                                }
                                if (assignedTable) return <span className="font-medium text-stone-700">@ {assignedTable.name}</span>;
                                return null;
                              })()}
                            </div>
                            {res.guest_phone && (
                              <p className="text-[11px] text-stone-500 mt-1"><Phone size={11} className="inline mr-0.5" />{res.guest_phone}</p>
                            )}
                            {res.notes && (
                              <div className="mt-1 flex items-start gap-1 text-[11px] text-stone-500">
                                <NotePencil size={11} className="mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-2">{res.notes}</span>
                              </div>
                            )}
                            <div className="flex gap-3 mt-2">
                              <button onClick={() => unassignReservation(res.id)} className="text-[10px] text-red-400 hover:text-red-600 transition-colors" data-testid={`unassign-res-${res.id}`}>
                                Unassign from table
                              </button>
                              <button onClick={() => deleteReservation(res.id)} className="text-[10px] text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-res-seated-${res.id}`}>
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Completed reservations */}
                  {floorReservations.filter((r) => r.status === "completed").length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold px-1 mb-1.5">Completed</p>
                      {floorReservations.filter((r) => r.status === "completed").map((res) => (
                        <div key={res.id} className="mb-1.5 p-2.5 rounded-lg border border-stone-100 bg-stone-50/50 opacity-60" data-testid={`res-completed-card-${res.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-stone-500">{res.guest_name}</span>
                            <Badge style={{ backgroundColor: "#57534E20", color: "#57534E", border: "none" }} className="text-[9px]">Done</Badge>
                          </div>
                          <p className="text-[10px] text-stone-500 mt-0.5">Party of {res.party_size} · {to12h(res.time)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ─── Canvas ─── */}
        <div
          ref={canvasRef}
          className="floor-canvas flex-1"
          style={{ cursor: isPanning ? "grabbing" : "default", overflow: "hidden", position: "relative" }}
          onClick={() => { if (!isPanning) setSelectedTable(null); }}
          onWheel={handleCanvasWheel}
          onMouseDown={handleCanvasPanStart}
          onMouseMove={handleCanvasPanMove}
          onMouseUp={handleCanvasPanEnd}
          onMouseLeave={handleCanvasPanEnd}
          onTouchStart={handleCanvasPanStart}
          onTouchMove={handleCanvasPanMove}
          onTouchEnd={handleCanvasPanEnd}
          data-testid="floor-canvas"
        >
          {/* Zoom controls */}
          <div className="absolute top-2 right-2 z-30 flex items-center gap-1 bg-white rounded-lg border border-stone-200 shadow-sm px-1 py-0.5" data-testid="zoom-controls">
            <button onClick={() => setCanvasZoom((z) => Math.max(0.3, z - 0.15))} className="w-7 h-7 flex items-center justify-center text-stone-500 hover:text-stone-800 text-lg font-medium">-</button>
            <span className="text-[10px] text-stone-500 font-mono min-w-[36px] text-center">{Math.round(canvasZoom * 100)}%</span>
            <button onClick={() => setCanvasZoom((z) => Math.min(3, z + 0.15))} className="w-7 h-7 flex items-center justify-center text-stone-500 hover:text-stone-800 text-lg font-medium">+</button>
            <button onClick={() => { setCanvasZoom(1); setCanvasPan({ x: 0, y: 0 }); }} className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-700 text-[10px]">Reset</button>
          </div>
          {/* Section Legend */}
          {sections.length > 0 && (
            <div className="absolute bottom-3 left-3 z-30 bg-white/95 backdrop-blur-sm rounded-lg border border-stone-200 shadow-md p-3" data-testid="section-legend">
              <p className="text-[9px] uppercase tracking-[0.15em] text-stone-500 font-bold mb-2">Sections</p>
              <div className="space-y-1.5">
                {sections.map((s) => {
                  const srv = servers.find((sv) => sv.id === s.server_id);
                  const tblCount = tables.filter((t) => t.section_id === s.id).length;
                  return (
                    <div key={s.id} className="flex items-center gap-2" data-testid={`legend-${s.id}`}>
                      <div className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color, boxShadow: `0 0 8px ${s.color}80` }} />
                      <span className="text-[11px] font-semibold text-stone-800">{srv ? srv.name : "Unassigned"}</span>
                      <span className="text-[9px] text-stone-400 font-medium">— {s.name} ({tblCount})</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasZoom})`, transformOrigin: "0 0", minWidth: 1200, minHeight: 800, position: "relative" }}>
          {/* Section labels (no region blob) */}
          {sections.map((s) => {
            const region = getSectionRegion(s);
            if (!region) return null;
            const sectionTableCount = tables.filter((t) => t.section_id === s.id).length;
            return (
              <div
                key={`region-${s.id}`}
                className="absolute pointer-events-none"
                style={{ left: region.minX, top: region.minY - 28, zIndex: 2 }}
                data-testid={`section-region-${s.name}`}
              >
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg shadow-sm" style={{ backgroundColor: s.color }}>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">{s.name}</span>
                  <span className="text-[9px] text-white/70">{sectionTableCount}</span>
                  {region.server && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-white/20 text-white">
                      {region.server.name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {tables.map((table) => {
            const section = getSectionForTable(table);
            return (
              <div
                key={table.id}
                className={`table-element ${dragState?.tableId === table.id ? "dragging" : ""} ${selectedTable?.id === table.id ? "selected" : ""} ${dropTargetTable === table.id ? "drop-target" : ""} ${assignMode ? "cursor-pointer" : ""} ${assignMode && assignSection && table.section_id === assignSection.id ? "assign-glow" : ""} ${table.status === "occupied" && tableTimers[table.id]?.elapsed_min <= 1 ? "just-seated" : ""}`}
                style={{
                  left: table.x, top: table.y,
                  width: table.width, height: table.height,
                  backgroundColor: (() => {
                    const base = STATUS_COLORS[table.status] || STATUS_COLORS.available;
                    if (table.status !== "occupied") return base;
                    // Capacity heat: darken based on fill ratio
                    const res = getResForTable(table.id);
                    if (!res) return base;
                    const ratio = Math.min(res.party_size / (table.seats || 4), 1);
                    // Interpolate: low fill = lighter, full = deeper
                    if (ratio >= 0.9) return "#C0392B";    // hot red - full
                    if (ratio >= 0.6) return "#D35400";    // warm orange
                    return "#E07A5F";                      // standard occupied
                  })(),
                  transform: `rotate(${table.rotation || 0}deg)`,
                  borderColor: dropTargetTable === table.id ? "#2F4F4F" : section ? section.color : "rgba(0,0,0,0.05)",
                  borderWidth: dropTargetTable === table.id ? "3px" : section ? "3px" : assignMode ? "2px" : "1px",
                  boxShadow: section && !assignMode ? `0 0 20px ${section.color}90, 0 0 8px ${section.color}70, inset 0 0 6px ${section.color}30` : "none",
                  ...(assignMode && assignSection && table.section_id === assignSection.id ? {
                    borderColor: assignSection.color,
                    borderWidth: "4px",
                  } : {}),
                  ...(assignMode && !assignSection ? { opacity: 0.8 } : {}),
                  ...(assignMode && assignSection && table.section_id !== assignSection.id ? { borderStyle: "dashed", borderColor: assignSection.color + "60", borderWidth: "2px" } : {}),
                  ...getTableShapeStyle(table),
                }}
                onMouseDown={(e) => { if (!assignMode) handleMouseDown(e, table); }}
                onTouchStart={(e) => { if (!assignMode) handleTouchStart(e, table); }}
                onClick={(e) => handleTableClick(e, table)}
                onDragOver={(e) => handleTableDragOver(e, table)}
                onDragLeave={handleTableDragLeave}
                onDrop={(e) => handleTableDrop(e, table)}
                data-testid={`table-element-${table.name}`}
              >
                {/* Seats around the table */}
                {(() => {
                  const res = getResForTable(table.id);
                  const partySize = res ? res.party_size : 0;
                  const combined = res ? getCombinedTables(res) : [];
                  const isCombined = combined.length > 1;
                  const combinedIndex = isCombined ? combined.findIndex((t) => t.id === table.id) : -1;
                  const isFirst = combinedIndex === 0;
                  const isLast = combinedIndex === combined.length - 1;
                  const isMiddle = isCombined && !isFirst && !isLast;

                  // For combined tables: skip seats on the joining edges
                  const positions = getSeatPositions(table);
                  const w = table.width || 80;
                  const h = table.height || 80;

                  // Calculate total seats across combined group & offset for this table
                  let seatOffset = 0;
                  if (isCombined) {
                    for (let ci = 0; ci < combinedIndex; ci++) {
                      seatOffset += (combined[ci].seats || 4);
                    }
                  }

                  return positions.map((pos, seatIdx) => {
                    // Filter out inner-edge seats for combined tables (horizontal layout)
                    if (isCombined && table.shape !== "round") {
                      const isRightEdge = pos.left > w;
                      const isLeftEdge = pos.left < 0;
                      // If not first table, skip left-edge seats (joining edge)
                      if (!isFirst && isLeftEdge) return null;
                      // If not last table, skip right-edge seats (joining edge)
                      if (!isLast && isRightEdge) return null;
                    }

                    const globalSeatIdx = seatOffset + seatIdx;
                    const isFilled = res && globalSeatIdx < partySize;
                    return (
                      <div
                        key={`seat-${seatIdx}`}
                        className="absolute rounded-full transition-colors"
                        style={{
                          width: 14, height: 14,
                          left: pos.left, top: pos.top,
                          backgroundColor: isFilled ? "#81B29A" : "#D6D3D1",
                          border: isFilled ? "2px solid #5A9A7A" : "2px solid #BFBDB8",
                          transform: `rotate(-${table.rotation || 0}deg)`,
                          zIndex: 50,
                        }}
                        data-testid={`seat-${table.name}-${seatIdx}`}
                      />
                    );
                  });
                })()}
                <span className="text-white font-semibold text-sm drop-shadow-sm" style={{ transform: `rotate(-${table.rotation || 0}deg)` }}>
                  {table.name}
                </span>
                <span className="text-white/80 text-[10px]" style={{ transform: `rotate(-${table.rotation || 0}deg)` }}>
                  {getResForTable(table.id) ? getResForTable(table.id).guest_name : `${table.seats} seats`}
                </span>
                {/* Party size badge */}
                {(() => {
                  const res = getResForTable(table.id);
                  if (!res) return null;
                  const combined = getCombinedTables(res);
                  return (
                    <div
                      className="absolute -top-2.5 -right-2.5 flex items-center justify-center rounded-full text-[10px] font-bold text-white shadow-md"
                      style={{
                        width: 22, height: 22,
                        backgroundColor: res.status === "seated" ? "#E07A5F" : "#F4A261",
                        transform: `rotate(-${table.rotation || 0}deg)`,
                        zIndex: 55,
                      }}
                      data-testid={`party-badge-${table.name}`}
                    >
                      {res.party_size}
                    </div>
                  );
                })()}
                {/* Combined table indicator */}
                {/* Turn Timer Ring */}
                {(() => {
                  const timer = tableTimers[table.id];
                  if (!timer || table.status !== "occupied") return null;
                  const min = timer.elapsed_min;
                  const defaultTurn = 45;
                  const progress = Math.min(min / defaultTurn, 1.5);
                  const ringColor = min >= 90 ? "#DC2626" : min >= 60 ? "#F59E0B" : min >= 45 ? "#F4A261" : "#22C55E";
                  const w = (table.width || 80) + 12;
                  const h = (table.height || 80) + 12;
                  const size = Math.max(w, h);
                  const r = size / 2 - 4;
                  const circ = 2 * Math.PI * r;
                  const dashOffset = circ * (1 - Math.min(progress, 1));
                  const label = min >= 60 ? `${Math.floor(min / 60)}h${min % 60 > 0 ? min % 60 : ""}` : `${min}m`;
                  return (
                    <>
                      <div className="turn-ring" style={{ width: size, height: size, top: -(size - (table.height || 80)) / 2, left: -(size - (table.width || 80)) / 2 }}>
                        <svg width={size} height={size}>
                          <circle className="turn-ring-bg" cx={size/2} cy={size/2} r={r} fill="none" strokeWidth="3" />
                          <circle className="turn-ring-progress" cx={size/2} cy={size/2} r={r} fill="none" strokeWidth="3.5"
                            stroke={ringColor} strokeLinecap="round"
                            strokeDasharray={circ} strokeDashoffset={dashOffset} />
                        </svg>
                      </div>
                      <div
                        className="absolute -bottom-2.5 -right-2.5 flex items-center justify-center rounded-full text-[8px] font-bold text-white shadow-md px-1"
                        style={{ minWidth: 22, height: 18, backgroundColor: ringColor, transform: `rotate(-${table.rotation || 0}deg)`, zIndex: 55 }}
                        data-testid={`timer-badge-${table.name}`}
                      >
                        {label}
                      </div>
                    </>
                  );
                })()}
                {/* Combined table connector */}
                {(() => {
                  const res = getResForTable(table.id);
                  if (!res) return null;
                  const combined = getCombinedTables(res);
                  if (combined.length <= 1) return null;
                  return (
                    <div
                      className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full text-[8px] font-bold text-white shadow-sm"
                      style={{ backgroundColor: "#2F4F4F", transform: `rotate(-${table.rotation || 0}deg)`, zIndex: 55 }}
                    >
                      {combined.length} combined
                    </div>
                  );
                })()}
                {/* Quick Note badge */}
                {allTableNotes[table.id]?.length > 0 && (
                  <div
                    className="absolute -top-2 -left-2 flex items-center justify-center rounded-full shadow-md"
                    style={{ width: 20, height: 20, backgroundColor: "#F4A261", transform: `rotate(-${table.rotation || 0}deg)`, zIndex: 55 }}
                    title={allTableNotes[table.id].map((n) => n.text).join(", ")}
                    data-testid={`note-badge-${table.name}`}
                  >
                    <NotePencil size={11} weight="bold" className="text-white" />
                  </div>
                )}
                {/* Resize handles (visible when selected) */}
                {selectedTable?.id === table.id && !dragState && (
                  <>
                    {["nw","ne","sw","se","n","s","e","w"].map((h) => (
                      <div
                        key={h}
                        className={`resize-handle resize-${h}`}
                        onMouseDown={(ev) => startResize(ev, table, h)}
                        data-testid={`resize-handle-${h}`}
                      />
                    ))}
                  </>
                )}
              </div>
            );
          })}

          {/* Combined table bridges */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
            {floorReservations.map((res) => {
              const combined = getCombinedTables(res);
              if (combined.length <= 1) return null;
              const bridges = [];
              for (let i = 0; i < combined.length - 1; i++) {
                const a = combined[i];
                const b = combined[i + 1];
                // Draw a solid bridge between adjacent tables
                const ax = a.x + a.width;
                const ay = a.y;
                const bx = b.x;
                const bridgeH = Math.min(a.height, b.height);
                const statusColor = STATUS_COLORS[a.status] || STATUS_COLORS.available;
                bridges.push(
                  <rect key={`${res.id}-bridge-${i}`}
                    x={ax} y={ay + 4} width={Math.max(4, bx - ax)} height={bridgeH - 8}
                    rx="3" fill={statusColor} opacity="0.8"
                  />
                );
              }
              return <g key={res.id}>{bridges}</g>;
            })}
          </svg>
          </div>
          {/* Floating toolbar - outside transform div */}
          <div className="floating-toolbar absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-xl z-20" data-testid="floating-toolbar">
            <Button size="sm" onClick={() => setShowAddTable(true)} style={{ backgroundColor: "#2F4F4F" }} data-testid="add-table-btn">
              <Plus size={16} className="mr-1" /> Add Table
            </Button>
            <div className="flex gap-1.5">
              {Object.entries(STATUS_COLORS).map(([status, color]) => (
                <div key={status} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                  <span className="text-[10px] text-stone-500 capitalize">{status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Side Panel (right on desktop, bottom sheet on mobile) ─── */}
        {selectedTable && (
          <div className="fixed bottom-16 left-0 right-0 md:static md:bottom-auto md:left-auto md:right-auto w-full md:w-[280px] max-h-[60vh] md:max-h-none bg-white border-t md:border-t-0 md:border-l border-stone-200 p-4 overflow-y-auto animate-slide-in z-20 rounded-t-2xl md:rounded-none shadow-[0_-4px_20px_rgba(0,0,0,0.1)] md:shadow-none" data-testid="table-detail-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Work Sans" }}>{selectedTable.name}</h3>
              <Badge style={{ backgroundColor: STATUS_COLORS[selectedTable.status], color: "#fff" }}>
                {selectedTable.status}
              </Badge>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Shape</span><span className="capitalize font-medium">{selectedTable.shape}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Seats</span><span className="font-medium">{selectedTable.seats}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Position</span><span className="font-medium">{Math.round(selectedTable.x)}, {Math.round(selectedTable.y)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Size</span><span className="font-medium">{Math.round(selectedTable.width)} &times; {Math.round(selectedTable.height)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Rotation</span><span className="font-medium">{selectedTable.rotation || 0}&deg;</span></div>
              {getSectionForTable(selectedTable) && (
                <div className="flex justify-between items-center">
                  <span className="text-stone-500">Section</span>
                  <span className="font-medium flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getSectionForTable(selectedTable).color }} />
                    {getSectionForTable(selectedTable).name}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-5 space-y-2">
              {isTableLocked(selectedTable.id) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => setUnlockedTables((prev) => new Set([...prev, selectedTable.id]))}
                  data-testid="unlock-table-btn"
                >
                  <Lock size={16} className="mr-1.5" /> Unlock Table
                </Button>
              )}
              {!isTableLocked(selectedTable.id) && unlockedTables.has(selectedTable.id) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-green-300 text-green-700 hover:bg-green-50"
                  onClick={() => setUnlockedTables((prev) => { const n = new Set(prev); n.delete(selectedTable.id); return n; })}
                  data-testid="lock-table-btn"
                >
                  <LockOpen size={16} className="mr-1.5" /> Lock Table
                </Button>
              )}
              <Button variant="outline" size="sm" className="w-full" onClick={() => rotateTable(selectedTable)} data-testid="rotate-table-btn">
                <ArrowClockwise size={16} className="mr-1.5" /> Rotate 45&deg;
              </Button>
              <Button variant="outline" size="sm" className="w-full" onClick={() => openEditTable(selectedTable)} data-testid="edit-table-btn">
                <PencilSimple size={16} className="mr-1.5" /> Edit Table
              </Button>
              <Button variant="outline" size="sm" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteTable(selectedTable.id)} data-testid="delete-table-btn">
                <Trash size={16} className="mr-1.5" /> Delete
              </Button>
            </div>
            {/* Reservation on this table */}
            {(() => {
              const res = getResForTable(selectedTable.id);
              if (!res) return null;
              return (
                <div className="mt-5 pt-4 border-t border-stone-100">
                  <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">
                    {res.type === "walk-in" ? "Walk-In" : "Reservation"}
                  </Label>
                  <div className="p-3 rounded-lg bg-stone-50 border border-stone-100 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm text-stone-800" style={{ fontFamily: "Work Sans" }}>{res.guest_name}</span>
                        <TierBadge tier={floorTiers[res.guest_name]} />
                      </div>
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: "#2F4F4F15", color: "#2F4F4F" }}>{to12h(res.time)}</span>
                    </div>
                    <div className="text-[11px] text-stone-500">Party of {res.party_size}{res.member_number ? ` · #${res.member_number}` : ""}</div>
                    {res.notes && <div className="text-[11px] text-stone-500">{res.notes}</div>}
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => unassignReservation(res.id)} className="text-[10px] text-stone-500 hover:text-stone-700 transition-colors" data-testid={`panel-unassign-res-${res.id}`}>
                        Unassign
                      </button>
                      <button onClick={() => deleteReservation(res.id)} className="text-[10px] text-red-400 hover:text-red-600 transition-colors" data-testid={`panel-delete-res-${res.id}`}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Quick Status Change */}
            <div className="mt-5">
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Quick Status</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.keys(STATUS_COLORS).map((s) => (
                  <button
                    key={s}
                    className={`text-xs py-1.5 rounded-md border capitalize transition-all ${selectedTable.status === s ? "ring-2 ring-offset-1" : "hover:opacity-80"}`}
                    style={{ backgroundColor: STATUS_COLORS[s] + "20", color: STATUS_COLORS[s], borderColor: STATUS_COLORS[s], ringColor: STATUS_COLORS[s] }}
                    onClick={async () => {
                      try {
                        await api.put(`/tables/${selectedTable.id}`, { status: s });
                        setTables((p) => p.map((t) => t.id === selectedTable.id ? { ...t, status: s } : t));
                        setSelectedTable({ ...selectedTable, status: s });
                        // Auto-sync reservation status based on table status change
                        const res = getResForTable(selectedTable.id);
                        if (res) {
                          if (s === "occupied" && (res.status === "confirmed" || res.status === "reserved")) {
                            await api.put(`/reservations/${res.id}`, { status: "seated" });
                          } else if (s === "available" && res.status === "seated") {
                            await api.put(`/reservations/${res.id}`, { status: "completed" });
                          }
                        }
                        toast.success(`Status: ${s}`);
                      } catch { toast.error("Failed"); }
                    }}
                    data-testid={`status-${s}-btn`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            {/* Table Notes */}
            <div className="mt-5 pt-4 border-t border-stone-100">
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Shift Notes</Label>
              <div className="space-y-1.5 max-h-[120px] overflow-y-auto mb-2">
                {tableNotes.map((n) => (
                  <div key={n.id} className="flex items-start gap-2 p-2 rounded-lg bg-stone-50 border border-stone-100 text-xs" data-testid={`table-note-${n.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-stone-700">{n.text}</p>
                      <p className="text-[9px] text-stone-300 mt-0.5">{n.author_name}</p>
                    </div>
                    <button onClick={() => deleteTableNote(n.id)} className="text-stone-300 hover:text-red-400 flex-shrink-0 mt-0.5" data-testid={`delete-note-${n.id}`}>
                      <Trash size={12} />
                    </button>
                  </div>
                ))}
                {tableNotes.length === 0 && <p className="text-[10px] text-stone-300">No notes for this table</p>}
              </div>
              <div className="flex gap-1.5">
                <Input
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 h-7 text-xs bg-black/40 border-white/10 text-white"
                  onKeyDown={(e) => e.key === "Enter" && addTableNote()}
                  data-testid="table-note-input"
                />
                <Button size="sm" className="h-7 px-2" style={{ backgroundColor: "#2F4F4F" }} onClick={addTableNote} data-testid="add-table-note-btn">
                  <Plus size={12} />
                </Button>
              </div>
            </div>
            {/* Per-Table Analytics */}
            {tableAnalytics && (
              <div className="mt-5 pt-4 border-t border-stone-100">
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3 flex items-center gap-1.5">
                  <ChartLineUp size={14} /> Performance (7d)
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2.5 rounded-lg bg-stone-50 text-center">
                    <p className="text-[10px] text-stone-500 uppercase">Avg Turn</p>
                    <p className="text-base font-bold" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }} data-testid="table-avg-turn">
                      {tableAnalytics.avg_turn_minutes > 0 ? `${tableAnalytics.avg_turn_minutes}m` : "--"}
                    </p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-stone-50 text-center">
                    <p className="text-[10px] text-stone-500 uppercase">Utilization</p>
                    <p className="text-base font-bold" style={{ fontFamily: "Work Sans", color: "#E07A5F" }} data-testid="table-utilization">
                      {tableAnalytics.utilization_percent}%
                    </p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-stone-50 text-center">
                    <p className="text-[10px] text-stone-500 uppercase">Turns</p>
                    <p className="text-base font-bold" style={{ fontFamily: "Work Sans", color: "#1C1917" }} data-testid="table-turns">
                      {tableAnalytics.total_turns}
                    </p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-stone-50 text-center">
                    <p className="text-[10px] text-stone-500 uppercase">Covers</p>
                    <p className="text-base font-bold" style={{ fontFamily: "Work Sans", color: "#81B29A" }} data-testid="table-covers">
                      {tableAnalytics.total_covers}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Bottom Floor Tabs (iPad/Mobile) ─── */}
      <div className="xl:hidden flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white border-t border-stone-200 overflow-x-auto" data-testid="floor-tabs-bottom">
        {floors.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveFloor(f.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${activeFloor === f.id ? "bg-[#2F4F4F] text-white shadow-sm" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
            data-testid={`floor-tab-bottom-${f.name.toLowerCase().replace(/\s/g, "-")}`}
          >
            {f.name}
          </button>
        ))}
        <button onClick={() => setShowAddFloor(true)} className="px-2 py-1.5 rounded-lg text-xs font-medium text-stone-400 hover:text-stone-700 bg-stone-50 whitespace-nowrap">
          <Plus size={12} className="inline mr-0.5" /> Floor
        </button>
        <button onClick={() => { setTemplateName(""); setTemplateDesc(""); setShowSaveTemplate(true); }} className="px-2 py-1.5 rounded-lg text-xs font-medium text-stone-400 hover:text-stone-700 bg-stone-50 whitespace-nowrap">
          <FloppyDisk size={12} className="inline mr-0.5" /> Save
        </button>
        <button onClick={() => { fetchTemplates(); setShowLoadTemplate(true); }} className="px-2 py-1.5 rounded-lg text-xs font-medium text-stone-400 hover:text-stone-700 bg-stone-50 whitespace-nowrap">
          <FolderOpen size={12} className="inline mr-0.5" /> Load
        </button>
      </div>

      {/* ─── Add Floor Dialog ─── */}
      <Dialog open={showAddFloor} onOpenChange={setShowAddFloor}>
        <DialogContent data-testid="add-floor-dialog">
          <DialogHeader><DialogTitle>Add Floor</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Floor Name</Label>
              <Input value={newFloorName} onChange={(e) => setNewFloorName(e.target.value)} placeholder="e.g. Patio" className="mt-1.5" data-testid="floor-name-input" />
            </div>
            <Button onClick={addFloor} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-floor-btn">Create Floor</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Add Table Dialog ─── */}
      <Dialog open={showAddTable} onOpenChange={setShowAddTable}>
        <DialogContent data-testid="add-table-dialog">
          <DialogHeader><DialogTitle>Add Table</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Table Name</Label>
              <Input value={newTableName} onChange={(e) => setNewTableName(e.target.value)} placeholder="e.g. T1" className="mt-1.5" data-testid="table-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Table Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {TABLE_PRESETS.map((p, i) => (
                  <button
                    key={i}
                    className={`flex flex-col items-center p-3 rounded-lg border transition-all text-xs ${newTablePreset === i ? "border-[#2F4F4F] bg-[#2F4F4F]/5 ring-1 ring-[#2F4F4F]" : "border-stone-200 hover:border-stone-300"}`}
                    onClick={() => setNewTablePreset(i)}
                    data-testid={`preset-${p.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {p.shape === "round" ? <CircleDashed size={22} /> : p.shape === "square" ? <Square size={22} /> : <Rectangle size={22} />}
                    <span className="mt-1 font-medium">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={addTable} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-table-btn">Add Table</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Edit Table Dialog ─── */}
      <Dialog open={showEditTable} onOpenChange={setShowEditTable}>
        <DialogContent data-testid="edit-table-dialog">
          <DialogHeader><DialogTitle>Edit Table</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Name</Label>
              <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5" data-testid="edit-table-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Seats</Label>
              <Input type="number" value={editForm.seats || 4} onChange={(e) => setEditForm({ ...editForm, seats: parseInt(e.target.value) })} className="mt-1.5" data-testid="edit-table-seats" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Shape</Label>
              <Select value={editForm.shape || "square"} onValueChange={(v) => setEditForm({ ...editForm, shape: v })}>
                <SelectTrigger className="mt-1.5" data-testid="edit-table-shape"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="round">Round</SelectItem>
                  <SelectItem value="square">Square</SelectItem>
                  <SelectItem value="rectangle">Rectangle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Section</Label>
              <Select value={editForm.section_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, section_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="edit-table-section"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Section</SelectItem>
                  {sections.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveEditTable} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-edit-table-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Add Section Dialog ─── */}
      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent data-testid="add-section-dialog">
          <DialogHeader><DialogTitle>Add Section</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Section Name</Label>
              <Input value={newSectionName} onChange={(e) => setNewSectionName(e.target.value)} placeholder="e.g. Patio" className="mt-1.5" data-testid="section-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Color</Label>
              <div className="flex gap-2 flex-wrap">
                {["#81B29A", "#E07A5F", "#F4A261", "#3D405B", "#E9C46A", "#264653", "#6A5ACD", "#20B2AA", "#CD5C5C", "#DAA520", "#4682B4", "#9370DB", "#3CB371", "#FF6B6B", "#48BFE3", "#7B2D8E"].map((c) => (
                  <button key={c} className={`w-8 h-8 rounded-full border-2 transition-all ${newSectionColor === c ? "ring-2 ring-offset-2" : ""}`} style={{ backgroundColor: c, borderColor: c, ringColor: c }} onClick={() => setNewSectionColor(c)} />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Assign Server</Label>
              <Select value={newSectionServer || "none"} onValueChange={(v) => setNewSectionServer(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="section-server-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {servers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addSection} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-section-btn">Create Section</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Smart Seating Dialog ─── */}
      <Dialog open={showSeatingSuggest} onOpenChange={setShowSeatingSuggest}>
        <DialogContent data-testid="smart-seating-dialog">
          <DialogHeader><DialogTitle>Smart Seating</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-3">
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold whitespace-nowrap">Party Size</Label>
              <Input type="number" min={1} max={20} value={partySize} onChange={(e) => setPartySize(parseInt(e.target.value) || 1)} className="w-20" data-testid="party-size-input" />
              <Button size="sm" onClick={getSeatingSuggestions} style={{ backgroundColor: "#2F4F4F" }} data-testid="suggest-btn">Find</Button>
            </div>
            {suggestions.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-4">No available tables found for this party size.</p>
            ) : (
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors">
                    <div>
                      <span className="font-medium text-sm">{s.name}</span>
                      <span className="text-xs text-stone-500 ml-2">{s.seats} seats &middot; {s.shape}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#81B29A20", color: "#81B29A" }}>
                        {s.score}% match
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Save Template Dialog ─── */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader><DialogTitle>Save Floor Layout</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-stone-500">Save the current layout of <span className="font-semibold text-stone-700">{floors.find((f) => f.id === activeFloor)?.name || "this floor"}</span> as a reusable template for future service days.</p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Template Name</Label>
              <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Friday Dinner Layout" className="mt-1.5" data-testid="template-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Description</Label>
              <Input value={templateDesc} onChange={(e) => setTemplateDesc(e.target.value)} placeholder="Optional notes" className="mt-1.5" data-testid="template-desc-input" />
            </div>
            <Button onClick={saveTemplate} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="confirm-save-template-btn">
              <FloppyDisk size={16} className="mr-1.5" /> Save Template
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Load Template Dialog ─── */}
      <Dialog open={showLoadTemplate} onOpenChange={setShowLoadTemplate}>
        <DialogContent data-testid="load-template-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Load Floor Layout</DialogTitle></DialogHeader>
          <div className="mt-2">
            <p className="text-xs text-stone-500 mb-4">Replace current layout of <span className="font-semibold text-stone-700">{floors.find((f) => f.id === activeFloor)?.name || "this floor"}</span> with a saved template. This will clear existing tables and sections.</p>
            {templates.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-6">No saved templates yet. Use "Save" to create one.</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {templates.map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors" data-testid={`template-item-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-stone-800">{t.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-stone-500">
                        <span>{t.table_count} tables</span>
                        <span>{t.section_count} sections</span>
                        {t.description && <span>&middot; {t.description}</span>}
                      </div>
                      <p className="text-[10px] text-stone-300 mt-0.5">
                        {new Date(t.created_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </div>
                    <div className="flex gap-1.5 ml-3">
                      <Button size="sm" onClick={() => loadTemplate(t.id)} style={{ backgroundColor: "#2F4F4F" }} data-testid={`load-template-${t.id}`}>Load</Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
